import React, { useEffect, useState, useRef } from "react";
import StationSearch from "./StationSearch";
import {
  Moon,
  Sun,
  Languages,
  Menu,
  X,
  Shield,
  FileText,
  Info,
  Heart,
} from "lucide-react";

const Header = ({
  theme = "light",
  language = "en",
  onAboutClick,
  onFavClick,
  onThemeToggle,
  onLanguageToggle,
  onPrivacyClick,
  onTermsClick,
  mapInstance,
}) => {
  const isDark = theme === "dark";
  const [langOpen, setLangOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeBtnRef = useRef(null);

  // viewport tracking
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // language popover outside-close
  useEffect(() => {
    if (!langOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest?.(".lang-popover")) setLangOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [langOpen]);

  // mobile menu: body scroll lock + focus + esc close
  useEffect(() => {
    if (menuOpen) {
      const bodyPrev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      setTimeout(() => closeBtnRef.current?.focus(), 0);

      const onKey = (e) => {
        if (e.key === "Escape") setMenuOpen(false);
      };
      document.addEventListener("keydown", onKey);

      return () => {
        document.body.style.overflow = bodyPrev;
        document.removeEventListener("keydown", onKey);
      };
    }
  }, [menuOpen]);

  // --- compact styles
  const NAV_HEIGHT = 44; // reduced height

  const headerStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: NAV_HEIGHT,
    backgroundColor: isDark ? "#0b0e12" : "#ffffff",
    borderBottom: isDark ? "1px solid #2d2d2d" : "1px solid #e5e5e5",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 12px", // tighter padding
    boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.5)" : "0 2px 8px rgba(0,0,0,0.06)",
    zIndex: 100,
  };

  const titleStyle = {
    fontSize: isMobile ? "0.9rem" : "1.1rem", // smaller title
    fontWeight: 700,
    color: isDark ? "#fff" : "#111",
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
    lineHeight: 1,
  };

  const centerWrap = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 6px",
  };

  const rightBtns = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    position: "relative",
  };

  const iconBtn = {
    background: "transparent",
    border: isDark ? "1px solid #2d2d2d" : "1px solid #e5e5e5",
    width: 30, // reduced from 35
    height: 30, // reduced from 35
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: isDark ? "#dbe3ee" : "#333",
    touchAction: "manipulation",
  };

  const popover = {
    position: "absolute",
    top: 38, // closer to button due to reduced btn size
    right: 0,
    background: isDark ? "#0b0e12" : "#ffffff",
    border: isDark ? "1px solid #2d2d2d" : "1px solid #e5e5e5",
    borderRadius: 10,
    boxShadow: isDark ? "0 6px 18px rgba(0,0,0,0.5)" : "0 6px 18px rgba(0,0,0,0.12)",
    padding: 6,
    minWidth: 130,
    zIndex: 10001,
  };

  // --- MOBILE SHEET
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    opacity: menuOpen ? 1 : 0,
    pointerEvents: menuOpen ? "auto" : "none",
    transition: "opacity 300ms ease",
    zIndex: 12000,
  };

  const panelStyle = {
    position: "fixed",
    top: 0,
    right: 0,
    width: "60%",
    maxWidth: 360,
    height: "100%",
    background: isDark
      ? "linear-gradient(145deg, rgba(15,18,25,0.98), rgba(25,28,35,0.95))"
      : "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(245,247,250,0.96))",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    boxShadow: isDark ? "0 0 25px rgba(0,0,0,0.8)" : "0 0 25px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    transform: menuOpen ? "translateX(0)" : "translateX(100%)",
    transition: "transform 340ms cubic-bezier(0.23, 1, 0.32, 1)",
    zIndex: 12010,
    borderLeft: isDark
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(200,200,200,0.2)",
    borderTopLeftRadius: 14,
    borderBottomLeftRadius: 14,
    overflow: "hidden",
  };

  const panelHeader = {
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
  };

  const panelTitle = {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: isDark ? "#eaeef7" : "#1b1b1f",
  };

  const panelContent = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: "1rem 1.2rem",
    overflowY: "auto",
  };

  const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 10,
    fontSize: "0.98rem",
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    background: "none",
    transition: "background 0.25s ease, transform 0.15s ease",
    color: isDark ? "#f0f4fc" : "#1b1d22",
  };

  const menuItemHover = (isDark) => ({
    background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
    transform: "scale(1.02)",
  });

  const footerStyle = {
    padding: "0.85rem 1.2rem",
    textAlign: "center",
    fontSize: "0.82rem",
    color: isDark ? "#9ea9b8" : "#6b6b75",
    borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
  };

  return (
    <>
      {/* spacer to prevent overlap with fixed header */}
      <div style={{ height: NAV_HEIGHT }} />

      <header style={headerStyle}>
        {/* LEFT: brand */}
        <div style={titleStyle}>Sanchaara</div>

        {/* CENTER: StationSearch (no extra wrappers) */}
        <div style={centerWrap}>
          <StationSearch theme={theme} language={language} mapInstance={mapInstance} />
        </div>

        {/* RIGHT: controls */}
        <div style={rightBtns}>
          {isMobile ? (
            <button
              style={iconBtn}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="app-menu"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          ) : (
            <>
              {/* Theme */}
              <button
                style={iconBtn}
                onClick={onThemeToggle}
                aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                title={isDark ? "Light" : "Dark"}
              >
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>

              {/* Language */}
              <div className="lang-popover" style={{ position: "relative" }}>
                <button
                  style={iconBtn}
                  onClick={() => setLangOpen((v) => !v)}
                  aria-haspopup="true"
                  aria-expanded={langOpen}
                  aria-label="Change Language"
                  title="Language"
                >
                  <Languages size={16} />
                </button>

                {langOpen && (
                  <div style={popover}>
                    {["en", "kn", "hi"].map((code) => (
                      <button
                        key={code}
                        onClick={() => {
                          onLanguageToggle?.(code);
                          setLangOpen(false);
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          background: "transparent",
                          border: "none",
                          color: isDark ? "#dbe3ee" : "#333",
                          textAlign: "left",
                          padding: "6px 8px",
                          fontWeight: language === code ? 800 : 600,
                          cursor: "pointer",
                          fontSize: "0.92rem",
                        }}
                      >
                        {code === "en" ? "English" : code === "hi" ? "हिंदी" : "ಕನ್ನಡ"}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Favourites (icon) */}
              <button
                style={iconBtn}
                onClick={onFavClick}
                aria-label="Favourites"
                title="Favourites"
              >
                <Heart size={16} />
              </button>

              {/* About (icon) */}
              <button
                style={iconBtn}
                onClick={onAboutClick}
                aria-label="About"
                title="About"
              >
                <Info size={16} />
              </button>
            </>
          )}
        </div>
      </header>

      {/* MOBILE MENU */}
      {isMobile && (
        <>
          <div
            style={overlayStyle}
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <aside
            id="app-menu"
            style={panelStyle}
            role="dialog"
            aria-modal="true"
            aria-label="App menu"
          >
            <div style={panelHeader}>
              <div style={panelTitle}>Menu</div>
              <button
                ref={closeBtnRef}
                style={iconBtn}
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>

            <div style={panelContent}>
              {[
                { label: "About", icon: <Info size={18} />, onClick: onAboutClick },
                { label: "Privacy", icon: <Shield size={18} />, onClick: onPrivacyClick },
                { label: "Terms", icon: <FileText size={18} />, onClick: onTermsClick },
              ].map(({ label, icon, onClick }) => (
                <button
                  key={label}
                  style={menuItemStyle}
                  onMouseEnter={(e) =>
                    Object.assign(e.currentTarget.style, menuItemHover(isDark))
                  }
                  onMouseLeave={(e) =>
                    Object.assign(e.currentTarget.style, {
                      background: "none",
                      transform: "none",
                    })
                  }
                  onClick={() => {
                    setMenuOpen(false);
                    onClick?.();
                  }}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </div>

            <div style={footerStyle}>Sanchaara © 2025</div>
          </aside>
        </>
      )}
    </>
  );
};

export default Header;
